<template>
  <div class="livekit-room">
    <!-- Indicador de grabación -->
    <div v-if="isRecording" class="recording-indicator">
      <span class="recording-dot"></span>
      Grabando...
    </div>

    <!-- Contenedor principal de video -->
    <div class="video-container" ref="videoContainer">
      <!-- Video local -->
      <div class="video-participant local-participant">
        <video ref="localVideo" autoplay muted playsinline></video>
        <div class="participant-info">
          <span>{{ participantName }}</span>
          <span v-if="!isAudioEnabled" class="muted-indicator">🔇</span>
        </div>
      </div>

      <!-- Videos remotos -->
      <div
        v-for="participant in remoteParticipants"
        :key="participant.identity"
        class="video-participant remote-participant"
      >
        <video
          :ref="`video-${participant.identity}`"
          autoplay
          playsinline
        ></video>
        <div class="participant-info">
          <span>{{ participant.name || participant.identity }}</span>
          <span v-if="participant.isMuted" class="muted-indicator">🔇</span>
        </div>
      </div>
    </div>

    <!-- Controles -->
    <div class="controls-bar">
      <button
        @click="toggleAudio"
        :class="['control-btn', { active: isAudioEnabled }]"
        :title="isAudioEnabled ? 'Silenciar' : 'Activar audio'"
      >
        <span v-if="isAudioEnabled">🎤</span>
        <span v-else>🔇</span>
      </button>

      <button
        @click="toggleVideo"
        :class="['control-btn', { active: isVideoEnabled }]"
        :title="isVideoEnabled ? 'Apagar video' : 'Encender video'"
      >
        <span v-if="isVideoEnabled">📹</span>
        <span v-else>📷</span>
      </button>

      <button
        v-if="showScreenShare"
        @click="toggleScreenShare"
        :class="['control-btn', { active: isScreenSharing }]"
        title="Compartir pantalla"
      >
        <span>🖥️</span>
      </button>

      <button
        v-if="showRecording && isModerator"
        @click="toggleRecording"
        :class="['control-btn', 'recording-btn', { recording: isRecording }]"
        :title="isRecording ? 'Detener grabación' : 'Iniciar grabación'"
      >
        <span v-if="isRecording">⏹️</span>
        <span v-else>⏺️</span>
      </button>

      <button @click="leaveRoom" class="control-btn leave-btn" title="Salir">
        <span>📞</span>
      </button>
    </div>
  </div>
</template>

<script>
import LiveKitService from "@/services/LiveKitService";
import {
  Room,
  RoomEvent,
  createLocalAudioTrack,
  createLocalVideoTrack,
} from "livekit-client";

export default {
  name: "LiveKitRoom",

  props: {
    // Nombre de la sala
    roomName: {
      type: String,
      required: true,
    },
    // Nombre del participante
    participantName: {
      type: String,
      required: true,
    },
    // Identidad única del participante
    participantIdentity: {
      type: String,
      required: true,
    },
    // Metadatos adicionales
    metadata: {
      type: String,
      default: "",
    },
    // Si es moderador
    isModerator: {
      type: Boolean,
      default: false,
    },
    // Mostrar botón de compartir pantalla
    showScreenShare: {
      type: Boolean,
      default: true,
    },
    // Mostrar botón de grabación
    showRecording: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      room: null,
      isAudioEnabled: true,
      isVideoEnabled: true,
      isScreenSharing: false,
      isRecording: false,
      remoteParticipants: [],
      audioTrack: null,
      videoTrack: null,
      screenTrack: null,
      egressId: null,
    };
  },

  async created() {
    await this.connectToRoom();
  },

  beforeDestroy() {
    this.disconnectFromRoom();
  },

  methods: {
    async connectToRoom() {
      try {
        // Obtener token del backend
        const connectionDetails = await LiveKitService.getConnectionDetails(
          this.roomName,
          this.participantName,
          this.participantIdentity,
          this.metadata,
          this.isModerator
        );

        // Crear y conectar a la sala
        this.room = new Room({
          adaptiveStream: true,
          dynacast: true,
        });

        // Configurar eventos
        this.setupRoomEvents();

        // Conectar
        await this.room.connect(connectionDetails.url, connectionDetails.token);

        // Publicar pistas locales
        await this.publishLocalTracks();

        // Verificar estado de grabación inicial
        if (this.showRecording) {
          await this.checkRecordingStatus();
        }

        this.$emit("connected", this.room);
      } catch (error) {
        console.error("Error conectando a LiveKit:", error);
        this.$emit("error", error);
      }
    },

    setupRoomEvents() {
      // Participante conectado
      this.room.on(RoomEvent.ParticipantConnected, (participant) => {
        console.log("Participante conectado:", participant.identity);
        this.updateRemoteParticipants();
        this.$emit("participant-connected", participant);
      });

      // Participante desconectado
      this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        console.log("Participante desconectado:", participant.identity);
        this.updateRemoteParticipants();
        this.$emit("participant-disconnected", participant);
      });

      // Pista recibida
      this.room.on(
        RoomEvent.TrackSubscribed,
        (track, publication, participant) => {
          console.log(
            "Pista recibida:",
            track.kind,
            "de",
            participant.identity
          );
          this.attachTrack(track, participant);
          this.updateRemoteParticipants();
        }
      );

      // Pista removida
      this.room.on(
        RoomEvent.TrackUnsubscribed,
        (track, publication, participant) => {
          console.log(
            "Pista removida:",
            track.kind,
            "de",
            participant.identity
          );
          this.detachTrack(track, participant);
          this.updateRemoteParticipants();
        }
      );

      // Cambio en metadata de participante
      this.room.on(RoomEvent.ParticipantMetadataChanged, () => {
        this.updateRemoteParticipants();
      });

      // Pista silenciada/cambiada
      this.room.on(RoomEvent.TrackMuted, () => {
        this.updateRemoteParticipants();
      });

      this.room.on(RoomEvent.TrackUnmuted, () => {
        this.updateRemoteParticipants();
      });

      // Desconectado
      this.room.on(RoomEvent.Disconnected, () => {
        console.log("Desconectado de la sala");
        this.$emit("disconnected");
      });
    },

    async publishLocalTracks() {
      // Crear y publicar pista de audio
      this.audioTrack = await createLocalAudioTrack();
      await this.room.localParticipant.publishTrack(this.audioTrack);

      // Crear y publicar pista de video
      this.videoTrack = await createLocalVideoTrack();
      await this.room.localParticipant.publishTrack(this.videoTrack);

      // Adjuntar video local
      this.videoTrack.attach(this.$refs.localVideo);
    },

    attachTrack(track, participant) {
      if (track.kind === "video") {
        this.$nextTick(() => {
          const videoRef = this.$refs[`video-${participant.identity}`];
          if (videoRef && videoRef[0]) {
            track.attach(videoRef[0]);
          }
        });
      } else if (track.kind === "audio") {
        const audioElement = document.createElement("audio");
        audioElement.id = `audio-${participant.identity}`;
        audioElement.autoplay = true;
        document.body.appendChild(audioElement);
        track.attach(audioElement);
      }
    },

    detachTrack(track, participant) {
      if (track.kind === "video") {
        const videoRef = this.$refs[`video-${participant.identity}`];
        if (videoRef && videoRef[0]) {
          track.detach(videoRef[0]);
        }
      } else if (track.kind === "audio") {
        const audioElement = document.getElementById(
          `audio-${participant.identity}`
        );
        if (audioElement) {
          track.detach(audioElement);
          audioElement.remove();
        }
      }
    },

    updateRemoteParticipants() {
      if (!this.room) return;

      this.remoteParticipants = Array.from(this.room.participants.values()).map(
        (p) => ({
          identity: p.identity,
          name: p.name,
          isMuted: this.isParticipantMuted(p),
        })
      );
    },

    isParticipantMuted(participant) {
      const audioPub = participant.getTrackPublication("audio");
      return audioPub ? audioPub.isMuted : true;
    },

    async toggleAudio() {
      if (!this.audioTrack) return;

      if (this.isAudioEnabled) {
        this.audioTrack.mute();
        this.isAudioEnabled = false;
      } else {
        this.audioTrack.unmute();
        this.isAudioEnabled = true;
      }

      this.$emit("audio-toggled", this.isAudioEnabled);
    },

    async toggleVideo() {
      if (!this.videoTrack) return;

      if (this.isVideoEnabled) {
        this.videoTrack.mute();
        this.isVideoEnabled = false;
      } else {
        this.videoTrack.unmute();
        this.isVideoEnabled = true;
      }

      this.$emit("video-toggled", this.isVideoEnabled);
    },

    async toggleScreenShare() {
      try {
        if (this.isScreenSharing) {
          // Detener compartir pantalla
          if (this.screenTrack) {
            this.room.localParticipant.unpublishTrack(this.screenTrack);
            this.screenTrack.stop();
            this.screenTrack = null;
          }
          this.isScreenSharing = false;
        } else {
          // Iniciar compartir pantalla
          this.screenTrack = await createLocalVideoTrack({
            source: "screen",
          });

          await this.room.localParticipant.publishTrack(this.screenTrack);
          this.isScreenSharing = true;

          // Detectar cuando el usuario deja de compartir
          this.screenTrack.on("ended", () => {
            this.toggleScreenShare();
          });
        }

        this.$emit("screen-share-toggled", this.isScreenSharing);
      } catch (error) {
        console.error("Error al compartir pantalla:", error);
        // El usuario canceló la selección de pantalla
        this.isScreenSharing = false;
      }
    },

    async toggleRecording() {
      try {
        if (this.isRecording) {
          // Detener grabación
          if (this.egressId) {
            await LiveKitService.stopRecording(this.egressId);
            this.egressId = null;
          }
          this.isRecording = false;
          this.$emit("recording-stopped");
        } else {
          // Iniciar grabación
          const result = await LiveKitService.startRecording(this.roomName);
          this.egressId = result.egressId;
          this.isRecording = true;
          this.$emit("recording-started", result);
        }
      } catch (error) {
        console.error("Error al gestionar grabación:", error);
        this.$emit("recording-error", error);
      }
    },

    async checkRecordingStatus() {
      try {
        const status = await LiveKitService.getRecordingStatus(this.roomName);
        this.isRecording = status.isRecording;
        if (status.isRecording && status.recordings.length > 0) {
          this.egressId = status.recordings[0].egressId;
        }
      } catch (error) {
        console.error("Error verificando estado de grabación:", error);
      }
    },

    async leaveRoom() {
      this.$emit("leave");
      await this.disconnectFromRoom();
    },

    async disconnectFromRoom() {
      if (this.audioTrack) {
        this.audioTrack.stop();
        this.audioTrack = null;
      }
      if (this.videoTrack) {
        this.videoTrack.stop();
        this.videoTrack = null;
      }
      if (this.screenTrack) {
        this.screenTrack.stop();
        this.screenTrack = null;
      }
      if (this.room) {
        await this.room.disconnect();
        this.room = null;
      }
    },

    // Método para finalizar el evento (remover todos los participantes)
    async finishEvent() {
      try {
        await LiveKitService.removeParticipants(this.roomName);
        await this.disconnectFromRoom();
        this.$emit("event-finished");
      } catch (error) {
        console.error("Error al finalizar evento:", error);
        throw error;
      }
    },

    // Método para silenciar a todos los participantes
    async muteAllParticipants() {
      try {
        await LiveKitService.muteAllParticipants(this.roomName);
        this.$emit("all-muted");
      } catch (error) {
        console.error("Error al silenciar a todos:", error);
        throw error;
      }
    },
  },
};
</script>

<style scoped>
.livekit-room {
  position: relative;
  width: 100%;
  height: 100%;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
}

.recording-indicator {
  position: absolute;
  top: 16px;
  left: 16px;
  background: rgba(255, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 100;
  font-weight: 600;
}

.recording-dot {
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.video-container {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 8px;
  padding: 8px;
  overflow-y: auto;
}

.video-participant {
  position: relative;
  background: #2a2a2a;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16/9;
}

.video-participant video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.local-participant {
  border: 2px solid #4caf50;
}

.participant-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 24px 8px 8px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.muted-indicator {
  font-size: 16px;
}

.controls-bar {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.8);
}

.control-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #3a3a3a;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.control-btn:hover {
  background: #4a4a4a;
  transform: scale(1.1);
}

.control-btn.active {
  background: #4caf50;
}

.control-btn.recording-btn {
  background: #666;
}

.control-btn.recording-btn.recording {
  background: #f44336;
  animation: pulse 1s infinite;
}

.control-btn.leave-btn {
  background: #f44336;
}

.control-btn.leave-btn:hover {
  background: #d32f2f;
}
</style>
